import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../../web/components-v2/Theme/theme';

// Design one-offs shared across the dashboard: stat numerals and card radii per the approved
// design (26px Sora values, 12px cards, 16px band) — deliberate departures from the base scale.
const STAT_VALUE_FONT_SIZE = 26;
const CARD_BORDER_RADIUS = 12;
const BAND_BORDER_RADIUS = 16;

export const reportsStyles = StyleSheet.create({
	// ---- Header ----
	header: {
		display: 'flex',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		flexWrap: 'wrap',
	},
	headerText: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	subtitle: {
		fontSize: THEME.typography.fontSizes.sm,
		color: THEME.colors.text.light,
	},
	headerActions: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},

	// ---- Report body ----
	content: {
		display: 'flex',
		flexDirection: 'column',
		gap: 18,
		minWidth: 0,
	},
	contentRefreshing: {
		opacity: 0.6,
	},

	// ---- Grids ----
	kpiGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(5, 1fr)',
		gap: THEME.spacing.md,
		[THEME.queries.lg]: {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
		[THEME.queries.sm]: {
			gridTemplateColumns: '1fr',
		},
	},
	splitRow: {
		display: 'grid',
		gridTemplateColumns: '1.6fr 1fr',
		gap: THEME.spacing.md,
		alignItems: 'stretch',
		[THEME.queries.lg]: {
			gridTemplateColumns: '1fr',
		},
	},

	// ---- Card chrome ----
	card: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_BORDER_RADIUS,
		padding: THEME.spacing.lg,
		boxSizing: 'border-box',
		boxShadow: `0 1px 3px ${THEME.colors.border.main}`,
		minWidth: 0,
	},
	cardHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: THEME.spacing.sm,
		marginBottom: THEME.spacing.md,
	},
	cardTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	cardSubtitle: {
		marginTop: 2,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	chartLoading: {
		padding: THEME.spacing.xl,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	chartEmpty: {
		padding: THEME.spacing.xl,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	errorCard: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_BORDER_RADIUS,
		padding: THEME.spacing.xl,
		textAlign: 'center',
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		boxShadow: `0 1px 3px ${THEME.colors.border.main}`,
	},

	// ---- KPI stat tiles ----
	statTile: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: CARD_BORDER_RADIUS,
		padding: THEME.spacing.md,
		boxShadow: `0 1px 3px ${THEME.colors.border.main}`,
		minWidth: 0,
	},
	statLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	statValue: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: STAT_VALUE_FONT_SIZE,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.2,
		color: THEME.colors.text.main,
	},
	statDelta: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	deltaGood: {
		color: THEME.colors.success.dark,
	},
	deltaBad: {
		color: THEME.colors.danger.main,
	},
	deltaNeutral: {
		color: THEME.colors.text.light,
	},
	skeletonText: {
		visibility: 'hidden',
	},

	// ---- Chart legend + tooltip ----
	legendRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
		flexShrink: 0,
	},
	legendItem: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	legendSwatch: {
		display: 'inline-block',
		width: 10,
		height: 3,
		borderRadius: 2,
	},
	legendSwatchCurrent: {
		backgroundColor: THEME.colors.chart.levBlue,
	},
	legendSwatchPrevious: {
		backgroundColor: THEME.colors.chart.aceGray,
	},
	chartTooltip: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: `0 1px 3px ${THEME.colors.border.main}`,
		padding: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
	},
	chartTooltipTitle: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	chartTooltipRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		color: THEME.colors.text.light,
	},

	// ---- Bar rows (first response buckets + categories) ----
	barList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	barRow: {
		display: 'grid',
		// The label column caps at 130px (per the design) so long category names truncate
		// instead of starving the bar track.
		gridTemplateColumns: 'minmax(64px, 130px) 1fr auto',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	barRowLabel: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.main,
		minWidth: 0,
		...THEME.common.truncate,
	},
	barRowValue: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
		whiteSpace: 'nowrap',
	},
	barTrack: {
		height: 8,
		borderRadius: 999,
		backgroundColor: THEME.colors.background.dark,
		overflow: 'hidden',
	},
	barFill: {
		height: '100%',
		borderRadius: 999,
		backgroundColor: THEME.colors.chart.levBlue,
	},
	categoryDot: {
		display: 'inline-block',
		width: 8,
		height: 8,
		borderRadius: '50%',
		flexShrink: 0,
	},
	headline: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: STAT_VALUE_FONT_SIZE,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.2,
		color: THEME.colors.text.main,
	},
	headlineHint: {
		marginTop: 2,
		marginBottom: THEME.spacing.md,
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},

	// ---- Top agents ----
	agentList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	agentRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	agentAvatar: {
		width: 32,
		height: 32,
		flexShrink: 0,
	},
	agentInfo: {
		flex: 1,
		minWidth: 0,
	},
	agentName: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		...THEME.common.truncate,
	},
	agentMeta: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	agentResolved: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
		whiteSpace: 'nowrap',
	},

	// ---- Support Agent impact band ----
	assistantBand: {
		borderRadius: BAND_BORDER_RADIUS,
		padding: 1.5,
		background: `linear-gradient(120deg, ${THEME.colors.primary.main}, ${THEME.colors.tertiary.dark})`,
	},
	assistantInner: {
		borderRadius: BAND_BORDER_RADIUS - 1.5,
		padding: THEME.spacing.lg,
		// Faint blue → white → pink wash over the card surface.
		background:
			`linear-gradient(120deg, ${THEME.colors.primary.light}, ${THEME.colors.background.light} 38%, ` +
			`${THEME.colors.background.light} 62%, ${THEME.colors.tertiary.light}), ${THEME.colors.background.light}`,
	},
	assistantHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
		marginBottom: THEME.spacing.md,
	},
	assistantBadge: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 32,
		height: 32,
		borderRadius: '50%',
		flexShrink: 0,
		background: `linear-gradient(135deg, ${THEME.colors.primary.main}, ${THEME.colors.tertiary.dark})`,
		color: THEME.colors.primary.contrastText,
	},
	assistantTitle: {
		margin: 0,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	assistantPill: {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '4px 10px',
		borderRadius: 999,
		border: `1px solid ${THEME.colors.tertiary.light}`,
		backgroundColor: THEME.colors.background.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.tertiary.dark,
	},
	assistantHint: {
		marginLeft: 'auto',
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	assistantStatsGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, 1fr)',
		gap: 1,
		backgroundColor: THEME.colors.border.light,
		[THEME.queries.md]: {
			gridTemplateColumns: 'repeat(2, 1fr)',
		},
	},
	assistantStatCell: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		padding: THEME.spacing.md,
		backgroundColor: THEME.colors.background.light,
		minWidth: 0,
	},
	assistantStatLabel: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.text.light,
	},
	assistantStatValue: {
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: STAT_VALUE_FONT_SIZE,
		fontWeight: THEME.typography.fontWeights.semibold,
		lineHeight: 1.2,
	},
	assistantStatPrimary: {
		color: THEME.colors.primary.main,
	},
	assistantStatSecondary: {
		color: THEME.colors.secondary.main,
	},
	assistantStatTertiary: {
		color: THEME.colors.tertiary.dark,
	},
	assistantStatSuccess: {
		color: THEME.colors.success.dark,
	},
});
